import clsx from 'clsx';
import { memo } from 'react';
import { useIntl } from 'react-intl';

import {
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import { formatTimeframeToParts } from './timeframe-formatter.js';
/**
 * Component properties interface for the {@link FormattedTimeframe} component.
 * @public
 */
export interface FormattedTimeframeProps extends StylingProps, DataTestId {
  /** Start date of the timeframe. */
  from: Date;
  /** End date of the timeframe. */
  to: Date;
}

/**
 * Component to format a timeframe leveraging React Intl APIs
 * @public
 */
export const FormattedTimeframe = memo((props: FormattedTimeframeProps) => {
  const intl = useIntl();
  const {
    from,
    to,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;
  const parts = formatTimeframeToParts(from, to, intl);
  return typeof parts === 'string' ? (
    <span
      data-testid={dataTestId}
      className={clsx('formatted-timeframe', consumerClassName)}
      style={consumerStyle}
    >
      {parts}
    </span>
  ) : (
    <span
      data-testid={dataTestId}
      className={clsx('formatted-timeframe', consumerClassName)}
      style={consumerStyle}
    >
      <span className="ft-prefix">{parts[0]}</span>
      <span className="ft-separator">&nbsp;{parts[1]}&nbsp;</span>
      <span className="ft-suffix">{parts[2]}</span>
    </span>
  );
});
